<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: number | string | null | undefined
  min?: number
  max?: number
  step?: number
  label?: string
  error?: string | null
  readonly?: boolean
  placeholder?: string
  showControls?: boolean,
  maxWidth?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
  name?: string
}>(), {
  showControls: true,
  maxWidth: '100%',
  size: 'md'
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'change', value: number): void
  (e: 'enter'): void
  (e: 'blur'): void
}>()

const localValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    const numValue = value === '' ? 0 : Number(value)
    if (!isNaN(numValue)) {
      emit('update:modelValue', numValue)
    }
  }
})

const increment = () => {
  const step = props.step || 1
  const currentValue = Number(localValue.value)
  if (!isNaN(currentValue)) {
    const newValue = currentValue + step
    if (props.max === undefined || newValue <= props.max) {
      emit('update:modelValue', newValue)
      emit('change', newValue)
    }
  }
}

const decrement = () => {
  const step = props.step || 1
  const currentValue = Number(localValue.value)
  if (!isNaN(currentValue)) {
    const newValue = currentValue - step
    if (props.min === undefined || newValue >= props.min) {
      emit('update:modelValue', newValue)
      emit('change', newValue)
    }
  }
}

const handleInput = (e: Event) => {
  const input = e.target as HTMLInputElement
  const value = input.value
  
  emit('change', Number(value))
  // Allow empty input
  if (value === '') {
    localValue.value = ''
    return
  }
  
  const numValue = Number(value)
  if (!isNaN(numValue)) {
    // Check min/max constraints
    if (props.min !== undefined && numValue < props.min) {
      localValue.value = props.min
    } else if (props.max !== undefined && numValue > props.max) {
      localValue.value = props.max
    } else {
      localValue.value = numValue
    }
  }
}

const handleBlur = () => {
  // If empty, set to min value or 0
  if (localValue.value === '') {
    localValue.value = props.min !== undefined ? props.min : 0
  }
  emit('blur')
}
</script>

<template>
  <div :style="{ maxWidth: maxWidth, width: !className ? '100%' : undefined }" :class="className">
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
    </label>
    
    <div class="relative flex">
      <!-- Decrement Button -->
      <button
        type="button"
        v-if="showControls == true"
        tabindex="-1"
        @click="decrement"
        :disabled="readonly || (min !== undefined && Number(modelValue) <= min)"
        class="flex-none px-3 border border-r-0 rounded-l-lg transition-colors"
        :class="[
          readonly 
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' 
            : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          size == 'sm' ? 'py-1' : 'py-2'
        ]"
      >
        -
      </button>

      <!-- Input Field -->
      <input
        type="number"
        :value="localValue"
        @input="handleInput"
        ref="numberInput"
        :id="name"
        @blur="handleBlur"
        @keyup.enter="$emit('enter')"
        :min="min"
        :max="max"
        :step="step"
        :readonly="readonly"
        :placeholder="placeholder"
        :style="{ maxWidth: maxWidth, width: '100%' }"
        class="flex-1 px-3 border text-gray-900 dark:text-white text-center"
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900',
          size == 'sm' ? 'py-1' : 'py-2'
        ]"
      />

      <!-- Increment Button -->
       <slot></slot>
      <button
        type="button"
        @click="increment"
        tabindex="-1"
        v-if="showControls == true"
        :disabled="readonly || (max !== undefined && Number(modelValue) >= max)"
        class="flex-none px-3 border border-l-0 rounded-r-lg transition-colors"
        :class="[
          readonly 
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed' 
            : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          size == 'sm' ? 'py-1' : 'py-2'
        ]"
      >
        +
      </button>
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>